import { useState } from 'react';
import { api, fmt, type QueueItem } from '../api.ts';
import SplitTable, { moveSides } from './SplitTable.tsx';

export interface SendResult {
  id: number; worker: string; stageNames: string; quantity: number;
  part: { id: number; jobNumber: string; quantity: number } | null;
  rest: { id: number; jobNumber: string; quantity: number } | null;
}

const today = () => new Date().toISOString().slice(0, 10);

/**
 * The goods leave. Quantity defaults to what the plan said, and anything less
 * than the order splits the job: the window says so, with the numbers, before
 * it happens, and the button names it ("Split and send out 5,000").
 */
export default function SendOutDialog({ item, onClose, onDone }: {
  item: QueueItem;
  onClose: () => void;
  onDone: (r: SendResult) => void;
}) {
  const run = Number(item.production_quantity);
  const ordered = Number(item.ordered_quantity);
  const planned = item.outside_planned_quantity ? Number(item.outside_planned_quantity) : run;
  const worker = item.outside_worker ?? 'the job worker';
  const stages = item.outside_stage_names ?? item.stage_name;
  const [qty, setQty] = useState(String(Math.min(planned, run)));
  const [sentOn, setSentOn] = useState(today());
  const [back, setBack] = useState('');
  const [challan, setChallan] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const q = Number(qty);
  const whole = qty.trim() !== '' && Number.isInteger(q) && q > 0;
  const problem = !whole ? '' : q > run ? `At most ${fmt(run)}` : '';
  const valid = whole && !problem && sentOn !== '';
  const splits = valid && q < ordered;

  async function go() {
    if (!valid) return;
    setBusy(true); setError('');
    try {
      const r = await api.post<SendResult>(`/api/outside/${item.outside_id}/send`, {
        quantity: q, sentOn, ...(back ? { expectedBack: back } : {}), ...(challan.trim() ? { challanNo: challan.trim() } : {}),
      });
      onDone(r);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not send it out');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="backdrop" onClick={() => !busy && onClose()}>
      <div className="modal" style={{ width: 460 }} role="dialog" aria-label={`Send out to ${worker}`} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Send out to {worker}</h3>
          <p>{item.job_number} · {stages}</p>
        </div>
        <div className="modal-body">
          {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}
          <div className="field-pair">
            <div className="field">
              <label htmlFor="so-qty">Quantity going out</label>
              <input id="so-qty" type="number" min="1" step="1" autoFocus value={qty}
                     onFocus={(e) => e.target.select()} onChange={(e) => setQty(e.target.value)} />
              <div className={problem ? 'hint pp-warn' : 'hint'}>
                {problem || `Run ${fmt(run)} · ordered ${fmt(ordered)}`}
              </div>
            </div>
            <div className="field">
              <label htmlFor="so-back">Expected back</label>
              <input id="so-back" type="date" value={back} onChange={(e) => setBack(e.target.value)} />
            </div>
          </div>
          <div className="field-pair">
            <div className="field">
              <label htmlFor="so-on">Sent on</label>
              <input id="so-on" type="date" value={sentOn} onChange={(e) => setSentOn(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="so-challan">Challan no. (optional)</label>
              <input id="so-challan" type="text" maxLength={40} value={challan} onChange={(e) => setChallan(e.target.value)} />
            </div>
          </div>

          {splits && (
            <>
              <SplitTable sides={moveSides(item.job_number, ordered, run, q)} ups={item.ups} />
              <div className="hint out-note">
                <strong>This splits the job.</strong> {item.job_number} keeps the rest and runs {stages} here.
                The new part goes to {worker}. You can undo this until work has started on either part.
              </div>
            </>
          )}
        </div>
        <div className="modal-foot">
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" onClick={() => void go()} disabled={busy || !valid}>
            {busy ? 'Sending…' : valid ? `${splits ? 'Split and send out' : 'Send out'} ${fmt(q)}` : 'Send out'}
          </button>
        </div>
      </div>
    </div>
  );
}
